import * as React from "react";
import { cn } from "@/lib/utils";
import { TypographySpacingProps, TypographySpacingPropsMapping, ColorProps } from "@/lib/fictoan-schema";

/**
 * Typography Component
 *
 * A collection of typography utility components for consistent text styling.
 * Based on shadcn/ui typography patterns.
 *
 * @example
 * ```tsx
 * <Typography.h1>Main Heading</Typography.h1>
 * <Typography.p>Regular paragraph text</Typography.p>
 *
 * // Enhanced components with props
 * <Heading1 weight="semibold" size="large" textColor="primary-strong">Main Heading</Heading1>
 * <Text size="small" isMuted>Small muted text</Text>
 *
 * // With Fictoan spacing props
 * <Heading1 padding="large" margin="medium">Spaced Heading</Heading1>
 * <Text padding="small" margin="tiny">Spaced Text</Text>
 * ```
 */

// Enhanced Typography Props
export type TypographyWeight = "regular" | "medium" | "semibold" | "bold";
export type TypographySize = "tiny" | "micro" | "small" | "medium" | "large" | "huge";
export interface EnhancedTypographyProps {
    weight?: TypographyWeight;
    size?: TypographySize;
    textColor?: ColorProps | "default";
}

export type TypographyTextColorProps = ColorProps | "default";

// Utility functions for class generation
const getWeightClass = (weight: TypographyWeight = "regular"): string => {
    const weightMap: Record<TypographyWeight, string> = {
        regular: "setu:!font-normal",
        medium: "setu:!font-medium",
        semibold: "setu:!font-semibold",
        bold: "setu:!font-bold",
    };
    return weightMap[weight];
};

const getSizeClass = (size: TypographySize = "medium", element: "heading" | "text"): string => {
    if (element === "heading") {
        const headingSizeMap: Record<TypographySize, string> = {
            tiny: "setu:!text-base", // 16px - smaller than micro but bigger than text
            micro: "setu:!text-lg", // 18px - small heading
            small: "setu:!text-xl", // 20px - H4 default
            medium: "setu:!text-2xl", // 24px - H3 default
            large: "setu:!text-3xl", // 30px - H2 default
            huge: "setu:!text-4xl", // 36px - H1 default
        };
        return headingSizeMap[size];
    } else {
        const textSizeMap: Record<TypographySize, string> = {
            tiny: "setu:!text-xs setu:!leading-4", // 12px - very small text
            micro: "setu:!text-xs setu:!leading-4", // 12px - fine print
            small: "setu:!text-sm setu:!leading-4", // 14px - compact text
            medium: "setu:!text-base", // 16px - default text
            large: "setu:!text-lg", // 18px - emphasized text
            huge: "setu:!text-xl", // 20px - large text
        };
        return textSizeMap[size];
    }
};

const getColorClass = (textColor: ColorProps | "default"): string => {
    if (textColor === "default") return "";
    return `setu:text-${textColor}`;
};

const getPaddingClass = ({
    padding,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
}: TypographySpacingProps): string => {
    if (!padding && !paddingTop && !paddingBottom && !paddingLeft && !paddingRight) return "";

    const paddingClass = padding ? TypographySpacingPropsMapping.padding[padding] : "";
    const paddingTopClass = paddingTop ? TypographySpacingPropsMapping.paddingTop[paddingTop] : "";
    const paddingBottomClass = paddingBottom ? TypographySpacingPropsMapping.paddingBottom[paddingBottom] : "";
    const paddingLeftClass = paddingLeft ? TypographySpacingPropsMapping.paddingLeft[paddingLeft] : "";
    const paddingRightClass = paddingRight ? TypographySpacingPropsMapping.paddingRight[paddingRight] : "";
    return `${paddingClass} ${paddingTopClass} ${paddingBottomClass} ${paddingLeftClass} ${paddingRightClass}`;
};

const getMarginClass = ({
    margin,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
}: TypographySpacingProps): string => {
    if (!margin && !marginTop && !marginBottom && !marginLeft && !marginRight) return "";

    const marginClass = margin ? TypographySpacingPropsMapping.margin[margin] : "";
    const marginTopClass = marginTop ? TypographySpacingPropsMapping.marginTop[marginTop] : "";
    const marginBottomClass = marginBottom ? TypographySpacingPropsMapping.marginBottom[marginBottom] : "";
    const marginLeftClass = marginLeft ? TypographySpacingPropsMapping.marginLeft[marginLeft] : "";
    const marginRightClass = marginRight ? TypographySpacingPropsMapping.marginRight[marginRight] : "";
    return `${marginClass} ${marginTopClass} ${marginBottomClass} ${marginLeftClass} ${marginRightClass}`;
};

// Enhanced Heading Components
export interface HeadingProps
    extends React.HTMLAttributes<HTMLHeadingElement>,
        EnhancedTypographyProps,
        TypographySpacingProps {}

// H1 Component (Legacy)
function H1({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
    return (
        <h1
            className={cn(
                "setu:scroll-m-20 setu:text-4xl setu:font-extrabold setu:tracking-tight setu:text-balance",
                className
            )}
            {...props}
        />
    );
}

// Heading1 Component (Enhanced)
function Heading1({
    className,
    weight = "semibold",
    size = "huge",
    textColor = "default",
    padding,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    margin,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    ...props
}: HeadingProps) {
    return (
        <h1
            className={cn(
                "setu:scroll-m-20 setu:tracking-tight setu:text-balance",
                getSizeClass(size, "heading"),
                getWeightClass(weight),
                getColorClass(textColor),
                getPaddingClass({ padding, paddingTop, paddingBottom, paddingLeft, paddingRight }),
                getMarginClass({ margin, marginTop, marginBottom, marginLeft, marginRight }),
                className
            )}
            {...props}
        />
    );
}

// H2 Component (Legacy)
function H2({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
    return (
        <h2
            className={cn(
                "setu:scroll-m-20 setu:pb-2 setu:text-3xl setu:font-semibold setu:tracking-tight setu:transition-colors setu:first:mt-0",
                className
            )}
            {...props}
        />
    );
}

// Heading2 Component (Enhanced)
function Heading2({
    className,
    weight = "semibold",
    size = "large",
    textColor = "default",
    padding,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    margin,
    ...props
}: HeadingProps) {
    return (
        <h2
            className={cn(
                "setu:scroll-m-20 setu:pb-2 setu:tracking-tight setu:transition-colors setu:first:mt-0",
                getSizeClass(size, "heading"),
                getWeightClass(weight),
                getColorClass(textColor),
                getPaddingClass({ padding, paddingTop, paddingBottom, paddingLeft, paddingRight }),
                getMarginClass({ margin, marginTop, marginBottom, marginLeft, marginRight }),
                className
            )}
            {...props}
        />
    );
}

// H3 Component (Legacy)
function H3({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
    return (
        <h3
            className={cn("setu:scroll-m-20 setu:text-2xl setu:font-semibold setu:tracking-tight", className)}
            {...props}
        />
    );
}

// Heading3 Component (Enhanced)
function Heading3({
    className,
    weight = "semibold",
    size = "medium",
    textColor = "default",
    padding,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    margin,
    ...props
}: HeadingProps) {
    return (
        <h3
            className={cn(
                "setu:scroll-m-20 setu:tracking-tight",
                getSizeClass(size, "heading"),
                getWeightClass(weight),
                getColorClass(textColor),
                getPaddingClass({ padding, paddingTop, paddingBottom, paddingLeft, paddingRight }),
                getMarginClass({ margin, marginTop, marginBottom, marginLeft, marginRight }),
                className
            )}
            {...props}
        />
    );
}

// H4 Component (Legacy)
function H4({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
    return (
        <h4
            className={cn("setu:scroll-m-20 setu:text-xl setu:font-semibold setu:tracking-tight", className)}
            {...props}
        />
    );
}

// Heading4 Component (Enhanced)
function Heading4({
    className,
    weight = "semibold",
    size = "small",
    textColor = "default",
    padding,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    margin,
    ...props
}: HeadingProps & TypographySpacingProps) {
    return (
        <h4
            className={cn(
                "setu:scroll-m-20 setu:tracking-tight",
                getSizeClass(size, "heading"),
                getWeightClass(weight),
                getColorClass(textColor),
                getPaddingClass({ padding, paddingTop, paddingBottom, paddingLeft, paddingRight }),
                getMarginClass({ margin, marginTop, marginBottom, marginLeft, marginRight }),
                className
            )}
            {...props}
        />
    );
}

// Heading5 Component (Enhanced)
function Heading5({
    className,
    weight = "semibold",
    size = "micro",
    textColor = "default",
    padding,
    margin,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    ...props
}: HeadingProps) {
    return (
        <h5
            className={cn(
                "setu:scroll-m-20 setu:tracking-tight",
                getSizeClass(size, "heading"),
                getWeightClass(weight),
                getColorClass(textColor),
                getPaddingClass({ padding, paddingTop, paddingBottom, paddingLeft, paddingRight }),
                getMarginClass({ margin, marginTop, marginBottom, marginLeft, marginRight }),
                className
            )}
            {...props}
        />
    );
}

// Heading6 Component (Enhanced)
function Heading6({
    className,
    weight = "semibold",
    size = "tiny",
    textColor = "default",
    padding,
    margin,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    ...props
}: HeadingProps) {
    return (
        <h6
            className={cn(
                "setu:scroll-m-20 setu:tracking-tight",
                getSizeClass(size, "heading"),
                getWeightClass(weight),
                getColorClass(textColor),
                getPaddingClass({ padding, paddingTop, paddingBottom, paddingLeft, paddingRight }),
                getMarginClass({ margin, marginTop, marginBottom, marginLeft, marginRight }),
                className
            )}
            {...props}
        />
    );
}

// Enhanced Text Components
export interface TextProps
    extends React.HTMLAttributes<HTMLParagraphElement>,
        EnhancedTypographyProps,
        TypographySpacingProps {
    isMuted?: boolean;
    isLead?: boolean;
}

// P Component (Legacy)
function P({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
    return <p className={cn("setu:leading-7", className)} {...props} />;
}

// Text Component (Enhanced)
function Text({
    className,
    weight = "regular",
    size = "medium",
    textColor = "default",
    isMuted = false,
    isLead = false,
    padding,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    margin,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    ...props
}: TextProps) {
    return (
        <p
            className={cn(
                "setu:leading-7",
                // Size and weight classes
                getSizeClass(size, "text"),
                getWeightClass(weight),
                // Color classes with priority order
                isMuted && "setu:!text-muted-foreground",
                !isMuted && getColorClass(textColor),
                // Lead styling
                isLead && "setu:!text-xl",
                // Spacing (only if not lead and default size)
                !isLead && size === "medium",
                // Fictoan spacing props
                getPaddingClass({ padding, paddingTop, paddingBottom, paddingLeft, paddingRight }),
                getMarginClass({ margin, marginTop, marginBottom, marginLeft, marginRight }),
                className
            )}
            {...props}
        />
    );
}

// Blockquote Component
function Blockquote({ className, ...props }: React.HTMLAttributes<HTMLQuoteElement>) {
    return <blockquote className={cn("setu:mt-6 setu:border-l-2 setu:pl-6 setu:italic", className)} {...props} />;
}

// Inline Code Component
function InlineCode({ className, ...props }: React.HTMLAttributes<HTMLElement>) {
    return (
        <code
            className={cn(
                "setu:bg-muted setu:relative setu:rounded setu:px-[0.3rem] setu:py-[0.2rem] setu:font-mono setu:text-sm setu:font-semibold",
                className
            )}
            {...props}
        />
    );
}

// Lead Component
function Lead({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
    return <p className={cn("setu:text-muted-foreground setu:text-xl", className)} {...props} />;
}

// Large Component
function Large({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
    return <div className={cn("setu:text-lg setu:font-semibold", className)} {...props} />;
}

// Small Component
function Small({ className, ...props }: React.HTMLAttributes<HTMLElement>) {
    return <small className={cn("setu:text-sm setu:leading-none setu:font-medium", className)} {...props} />;
}

// Muted Component
function Muted({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
    return <p className={cn("setu:text-muted-foreground setu:text-sm", className)} {...props} />;
}

// List Component
function List({ className, ...props }: React.HTMLAttributes<HTMLUListElement>) {
    return <ul className={cn("setu:my-6 setu:ml-6 setu:list-disc setu:[&>li]:mt-2", className)} {...props} />;
}

// Pre Component
function Pre({ className, ...props }: React.HTMLAttributes<HTMLPreElement>) {
    return (
        <pre
            className={cn(
                "setu:mb-4 setu:mt-4 setu:overflow-x-auto setu:rounded-lg setu:bg-black setu:py-4",
                className
            )}
            {...props}
        />
    );
}

// Code Component
function Code({ className, ...props }: React.HTMLAttributes<HTMLElement>) {
    return (
        <code
            className={cn(
                "setu:relative setu:rounded setu:bg-muted setu:px-[0.3rem] setu:py-[0.2rem] setu:font-mono setu:text-sm setu:font-semibold",
                className
            )}
            {...props}
        />
    );
}

// Table Component
function Table({ className, ...props }: React.HTMLAttributes<HTMLTableElement>) {
    return (
        <div className="setu:my-6 setu:w-full setu:overflow-y-auto">
            <table className={cn("w-full", className)} {...props} />
        </div>
    );
}

// Table Header Component
function TableHeader({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
    return <thead className={cn("setu:even:bg-muted setu:m-0 setu:border-t setu:p-0", className)} {...props} />;
}

// Table Body Component
function TableBody({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
    return <tbody className={cn("setu:even:bg-muted setu:m-0 setu:border-t setu:p-0", className)} {...props} />;
}

// Table Row Component
function TableRow({ className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) {
    return <tr className={cn("setu:even:bg-muted setu:m-0 setu:border-t setu:p-0", className)} {...props} />;
}

// Table Head Component
function TableHead({ className, ...props }: React.HTMLAttributes<HTMLTableCellElement>) {
    return (
        <th
            className={cn(
                "setu:border setu:px-4 setu:py-2 setu:text-left setu:font-bold setu:[&[align=center]]:text-center setu:[&[align=right]]:text-right",
                className
            )}
            {...props}
        />
    );
}

// Table Cell Component
function TableCell({ className, ...props }: React.HTMLAttributes<HTMLTableCellElement>) {
    return (
        <td
            className={cn(
                "setu:border setu:px-4 setu:py-2 setu:text-left setu:[&[align=center]]:text-center setu:[&[align=right]]:text-right",
                className
            )}
            {...props}
        />
    );
}

// Legacy Typography exports
export const Typography = {
    H1,
    H2,
    H3,
    H4,
    P,
    Blockquote,
    InlineCode,
    Lead,
    Large,
    Small,
    Muted,
    List,
    Pre,
    Code,
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
};

// Enhanced component exports
export {
    Heading1,
    Heading2,
    Heading3,
    Heading4,
    Heading5,
    Heading6,
    Text,
    // Legacy components for backward compatibility
    H1,
    H2,
    H3,
    H4,
    P,
    Blockquote,
    InlineCode,
    Lead,
    Large,
    Small,
    Muted,
    List,
    Pre,
    Code,
};
